import {$dfs} from '@lexical/utils';
import {$isImageNode, ImageNode} from 'components/DSYS/editor/nodes/ImageNode';
import {LexicalEditor} from 'lexical';

/** Hosts whose images we copy into our own storage after a paste (see news_media_import.py). */
export const REHOSTABLE_IMAGE_HOSTS: readonly string[] = [
  'paper-attachments.dropboxusercontent.com',
];

const PRELOAD_TIMEOUT_MS = 15_000;

/** URLs currently being imported per editor, so back-to-back pastes don't import twice. */
const inFlight = new WeakMap<LexicalEditor, Set<string>>();

export const isRehostableImageUrl = (src: string) => {
  try {
    const url = new URL(src);
    return url.protocol === 'https:' && REHOSTABLE_IMAGE_HOSTS.includes(url.hostname.toLowerCase());
  } catch {
    return false;
  }
};

const $imageNodes = (): ImageNode[] =>
  $dfs()
    .map(({node}) => node)
    .filter($isImageNode);

const collectRehostableSrcs = (editor: LexicalEditor): string[] =>
  editor.getEditorState().read(() => [
    ...new Set(
      $imageNodes()
        .map((n) => n.getSrc())
        .filter(isRehostableImageUrl)
    ),
  ]);

/** Resolve once the browser has the image cached, so swapping `src` doesn't flash. */
const preload = (src: string) =>
  new Promise<void>((resolve) => {
    const img = new Image();
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
    window.setTimeout(done, PRELOAD_TIMEOUT_MS);
    img.src = src;
  });

/**
 * Copies every image in the document that still points at an external host (e.g. Paper's
 * attachment CDN) into our media storage and repoints the nodes. Each distinct URL is imported
 * once. Failures leave the original URL in place, which still renders.
 */
export const rehostPastedImages = async (
  editor: LexicalEditor,
  importMediaFromUrl: (url: string) => Promise<string>,
  getMediaUrl: (asset: string) => string,
  onError?: (src: string, error: unknown) => void
): Promise<void> => {
  const pending = inFlight.get(editor) ?? new Set<string>();
  inFlight.set(editor, pending);
  await Promise.all(
    collectRehostableSrcs(editor)
      .filter((src) => !pending.has(src))
      .map(async (src) => {
        pending.add(src);
        try {
          const finalUrl = getMediaUrl(await importMediaFromUrl(src));
          await preload(finalUrl);
          editor.update(() => {
            $imageNodes()
              .filter((node) => node.getSrc() === src)
              .forEach((node) => node.setSrc(finalUrl));
          });
        } catch (error) {
          onError?.(src, error);
        } finally {
          pending.delete(src);
        }
      })
  );
};
